import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Card, CardContent, Typography, Chip, Stack, Tabs, Tab, Button, Alert,
  IconButton, Skeleton, MenuItem, Select, FormControl, InputLabel,
  Snackbar,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { KpiCard, ChartWidget, TableWidget, ChartSpec, TableSpec, CardSpec } from "../components/DashboardWidget";
import { useDashboardFilters } from "../context/DashboardFiltersContext";

interface DashPage {
  id: string;
  label: string;
  visible_filters: string[];
  charts: Array<{ id: string; title: string; type: string }>;
  tables: Array<{ id: string; title: string }>;
  cards: number;
}

interface FilterDefinition {
  filter_id: string;
  label?: string;
  type?: string;
  pages?: string[];
  options?: Array<{ value: string; label: string }>;
}

interface Month { value: string; label: string; }

interface Metadata {
  id: string;
  title: string;
  tenant: string;
  default_page: string;
  pages: DashPage[];
  filters: FilterDefinition[];
  months: Month[];
  filter_catalog: Record<string, Array<{ value: string; label: string }>>;
  stale: boolean;
  error?: string;
}

interface PageData {
  page: string;
  title?: string;
  cards: Array<CardSpec>;
  datasets: Record<string, Array<Record<string, any>>>;
  charts: Array<ChartSpec>;
  tables: Array<TableSpec>;
  visible_filters: string[];
  stale: boolean;
  error?: string;
  // O mês corrente, quando ele ainda não fechou. O backend já calculava isto
  // e só a Sophia usava — a tela mostrava o número e calava o recorte.
  periodo_parcial?: {
    year_month: string;
    dias_cobertos: number;
    dias_no_mes: number;
    atraso_dias?: number;
    aviso?: string;
  } | null;
}

export function DashboardViewer() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const api = useApi();
  const navigate = useNavigate();
  const { filters, setPage, setFilterKeys, setFilterValue, clearFilters, applyPatch, markReady } = useDashboardFilters();

  const metaQ = useQuery({
    queryKey: ["v2-dash-metadata", tenant, id],
    queryFn: () => api.call<Metadata>(`/api/v2/dashboards/${encodeURIComponent(id ?? "")}/metadata`),
    enabled: !!tenant && !!id, staleTime: 60_000, retry: false,
  });
  const meta = metaQ.data;

  // Populate filterKeys whitelist in the context — Sophia's applyPatch uses
  // this to accept only known filter ids from the LLM output.
  useEffect(() => {
    if (meta?.filters?.length) {
      setFilterKeys(meta.filters.map((f) => f.filter_id));
    }
  }, [meta?.filters, setFilterKeys]);

  // Also derive selected month from context.end_month (Sophia can push it)
  const selectedMonth = filters.end_month || "";

  // If the dashboard belongs to a different tenant than the one currently
  // selected, redirect back to the list — server also returns 403
  // tenant_mismatch, but this bounces the user immediately with UX
  // feedback rather than showing a stale render.
  useEffect(() => {
    const err = metaQ.error as any;
    if (err?.status === 403 && err?.error === "tenant_mismatch") {
      navigate("/dashboards", { replace: true });
    }
  }, [metaQ.error, navigate]);
  // Same when tenant switches while we're on a dashboard already loaded:
  // meta.tenant no longer matches the header tenant → bounce.
  useEffect(() => {
    if (meta && tenant && meta.tenant && meta.tenant !== tenant) {
      navigate("/dashboards", { replace: true });
    }
  }, [meta?.tenant, tenant, navigate]); // eslint-disable-line

  // pick default page from metadata (first time)
  useEffect(() => {
    if (meta?.default_page && !meta.pages.some((p) => p.id === filters.page)) {
      setPage(meta.default_page);
    }
  }, [meta?.id]); // eslint-disable-line

  useEffect(() => {
    if (meta?.months?.length && !filters.end_month) {
      applyPatch({ end_month: meta.months[0].value });
    }
  }, [meta?.months, filters.end_month, applyPatch]);

  const activePage = meta?.pages.find((p) => p.id === filters.page) ?? meta?.pages[0];
  const activePageId = activePage?.id ?? meta?.default_page ?? "";

  const state = useMemo(() => ({
    page: activePageId,
    start_month: filters.start_month,
    end_month: filters.end_month,
    // O backend lê os recortes em `state.filters` (build_ui_state). Espalhados no
    // topo, como estavam, a escolha do usuário nunca chegava na consulta e a
    // página continuava mostrando a empresa inteira.
    filters: filters.filters,
    ...filters.filters,  // compatibilidade: Sophia ainda lê as chaves no topo
  }), [activePageId, filters.start_month, filters.end_month, filters.filters]);

  const dataQ = useQuery({
    queryKey: ["v2-dash-page", tenant, id, activePageId, filters.start_month, filters.end_month, filters.filters],
    queryFn: () => api.call<PageData>(
      `/api/v2/dashboards/${encodeURIComponent(id ?? "")}/page-data`,
      { method: "POST", body: JSON.stringify({ page_id: activePageId, state }), headers: { "Content-Type": "application/json" } },
    ),
    enabled: !!tenant && !!id && !!activePageId, staleTime: 30_000,
  });
  const pageData = dataQ.data;

  // Avisa a Sophia (pelo contexto) que o dashboard terminou de recarregar.
  //
  // O aviso e disparado UMA VEZ por carga, ancorado em `dataUpdatedAt` — um
  // numero que so anda quando chega dado novo. A versao anterior dependia da
  // identidade de `markReady` e de `dataQ.data`: markReady() incrementava o
  // contador do contexto, o contexto devolvia outra funcao, o efeito rodava
  // de novo e chamava markReady() outra vez. Era laco infinito, e o sintoma
  // nao parecia um laco: a aba do dashboard congelava e o menu lateral
  // deixava de responder (a thread principal nunca voltava), entao sair do
  // dashboard so com F5.
  const ultimoAviso = useRef(0);
  useEffect(() => {
    if (!dataQ.data || dataQ.isFetching) return;
    if (ultimoAviso.current === dataQ.dataUpdatedAt) return;
    ultimoAviso.current = dataQ.dataUpdatedAt;
    markReady();
  }, [dataQ.data, dataQ.isFetching, dataQ.dataUpdatedAt, markReady]);

  const activeFilters = (activePage?.visible_filters ?? [])
    .map((fid) => meta?.filters.find((f) => f.filter_id === fid))
    .filter(Boolean) as FilterDefinition[];

  function updateFilter(filterId: string, value: string) {
    setFilterValue(filterId, value ? [value] : []);
  }

  const [exportToast, setExportToast] = useState<string | null>(null);

  function toCsv(rows: Array<Record<string, any>>): string {
    if (!rows.length) return "";
    const colSet = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => colSet.add(k)));
    const cols = Array.from(colSet);
    const esc = (v: any) => {
      if (v == null) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return /[",;\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.join(";");
    const body = rows.map((r) => cols.map((c) => esc(r[c])).join(";")).join("\n");
    // BOM utf-8 pro Excel abrir com acentos ok
    return "﻿" + header + "\n" + body;
  }

  function handleExport() {
    if (!pageData || !meta) {
      setExportToast("Sem dados para exportar");
      return;
    }
    const parts: string[] = [];
    let sheets = 0;
    // Cards → key-value
    if (pageData.cards?.length) {
      const cardRows = pageData.cards.map((c: any) => ({
        card_id: c.card_id ?? "",
        label: c.label ?? "",
        value: c.value ?? c.value_formatted ?? "",
        change: c.change_pct ?? c.delta ?? "",
      }));
      parts.push(`### KPIs\n${toCsv(cardRows)}`);
      sheets++;
    }
    // Datasets → cada um vira uma seção
    const datasets = pageData.datasets ?? {};
    for (const [name, rows] of Object.entries(datasets)) {
      if (Array.isArray(rows) && rows.length > 0) {
        parts.push(`### ${name}\n${toCsv(rows as any[])}`);
        sheets++;
      }
    }
    if (!parts.length) {
      setExportToast("Página sem dados para exportar");
      return;
    }
    const csv = parts.join("\n\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const pageLabel = activePage?.label?.replace(/[^a-z0-9]+/gi, "-").toLowerCase() ?? activePageId ?? "pagina";
    const monthTag = filters.end_month || "atual";
    a.href = url;
    a.download = `${id ?? "dashboard"}-${pageLabel}-${monthTag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportToast(`CSV baixado (${sheets} seção${sheets === 1 ? "" : "ões"})`);
  }

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          component={RouterLink}
          to="/dashboards"
          aria-label="Voltar para dashboards"
          sx={{ width: 44, height: 44 }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">Dashboards</Typography>
      </Stack>

      <PageHeader
        title={meta?.title ?? id ?? "Dashboard"}
        subtitle={<>Tenant: <strong>{tenant ?? "?"}</strong> · base padronizada</>}
        action={
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, width: { xs: "100%", sm: "auto" } }}>
            <Button
              startIcon={<RefreshRoundedIcon />}
              variant="outlined"
              onClick={() => { void metaQ.refetch(); void dataQ.refetch(); }}
              disabled={dataQ.isFetching || metaQ.isFetching}
              sx={{ minHeight: 44, flex: { xs: 1, sm: "unset" } }}
            >
              {(dataQ.isFetching || metaQ.isFetching) ? "Atualizando..." : "Atualizar"}
            </Button>
            <Button
              startIcon={<FileDownloadRoundedIcon />}
              variant="contained"
              onClick={handleExport}
              disabled={!pageData || dataQ.isFetching}
              sx={{ minHeight: 44, flex: { xs: 1, sm: "unset" } }}
            >
              Exportar
            </Button>
          </Stack>
        }
      />

      {metaQ.error && <Alert severity="error">Falha ao carregar dashboard.</Alert>}
      {meta?.stale && <Alert severity="warning">Dashboard em cache ou fora do ar ({meta.error ?? "unknown"}).</Alert>}
      {pageData?.stale && !meta?.stale && (
        <Alert severity="warning">Página em cache ou fora do ar ({pageData.error ?? "unknown"}).</Alert>
      )}

      <Card variant="outlined">
        {/* Page tabs (from catalog) */}
        {metaQ.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton width={400} height={40} /></Box>
        ) : (
          <Tabs
            value={Math.max(0, meta?.pages.findIndex((p) => p.id === activePageId) ?? 0)}
            onChange={(_, v) => meta?.pages[v] && setPage(meta.pages[v].id)}
            variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {(meta?.pages ?? []).map((p) => <Tab key={p.id} label={p.label} />)}
          </Tabs>
        )}

        <CardContent>
          {/* Filter bar */}
          {(meta?.months?.length || activeFilters.length > 0) && (
            <Box
              sx={{
                mb: 2.5,
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(180px, 1fr))" },
              }}
            >
              {meta?.months && meta.months.length > 0 && (
                <FormControl size="small" fullWidth>
                  {/* o `id`/`labelId` dá nome acessível ao campo: sem ele, leitor
                      de tela (e teste automatizado) vê uma caixa sem rótulo */}
                  <InputLabel id="filtro-mes-label">Mês</InputLabel>
                  <Select
                    labelId="filtro-mes-label"
                    value={selectedMonth}
                    label="Mês"
                    onChange={(e) => applyPatch({ end_month: e.target.value })}
                  >
                    {meta.months.slice(0, 24).map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {activeFilters.slice(0, 6).map((f) => {
                const opts = (meta?.filter_catalog?.[f.filter_id]) ?? f.options ?? [];
                return (
                  <FormControl key={f.filter_id} size="small" fullWidth>
                    <InputLabel id={`filtro-${f.filter_id}-label`}>{f.label ?? f.filter_id}</InputLabel>
                    <Select
                      labelId={`filtro-${f.filter_id}-label`}
                      value={filters.filters[f.filter_id]?.[0] ?? ""}
                      label={f.label ?? f.filter_id}
                      onChange={(e) => updateFilter(f.filter_id, e.target.value)}
                    >
                      <MenuItem value=""><em>todos</em></MenuItem>
                      {opts.slice(0, 100).map((o: any) => (
                        <MenuItem key={o.value} value={o.value}>{o.label ?? o.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              })}
              {Object.keys(filters.filters).length > 0 && (
                <Chip
                  label="Limpar filtros"
                  onDelete={() => clearFilters()}
                  onClick={() => clearFilters()}
                  sx={{ height: 40, alignSelf: "center", justifySelf: { xs: "start", sm: "unset" } }}
                />
              )}
            </Box>
          )}

          {/* Cards */}
          {(pageData?.cards?.length ?? 0) > 0 && (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(${Math.min(pageData?.cards.length || 1, 4)}, 1fr)` },
              gap: 2, mb: 3,
            }}>
              {pageData?.cards.map((c, i) => <KpiCard key={c.card_id ?? i} spec={c} />)}
            </Box>
          )}

          {pageData?.periodo_parcial && (
            // MÊS ABERTO NÃO SE COMPARA COM MÊS FECHADO.
            //
            // Medido na Rejuntamix em 04/11/2026: o card dizia "FATURAMENTO
            // R$ 309k" ao lado de um gráfico onde todo mês anterior passava de
            // R$ 12M. O leitor vê uma queda de 97%; o que havia era um mês com
            // três dias. O backend já mandava `periodo_parcial` com os dias
            // cobertos — e só a Sophia lia.
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              <strong>
                {pageData.periodo_parcial.year_month} ainda está aberto:
                dados até o dia {pageData.periodo_parcial.dias_cobertos} de{" "}
                {pageData.periodo_parcial.dias_no_mes}.
              </strong>{" "}
              Os números deste mês não são comparáveis com um mês fechado — a
              diferença contra o mês anterior não é queda.
            </Alert>
          )}
          {dataQ.isLoading && Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={80} sx={{ mb: 1 }} />
          ))}

          {/* Charts */}
          {(pageData?.charts?.length ?? 0) > 0 && (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: pageData!.charts.length > 1 ? "1fr 1fr" : "1fr" },
              gap: 2, mb: 2,
            }}>
              {pageData!.charts.map((chart) => (
                <ChartWidget key={chart.chart_id} spec={chart} dataset={pageData!.datasets[chart.dataset]} />
              ))}
            </Box>
          )}

          {/* Tables */}
          {(pageData?.tables?.length ?? 0) > 0 && (
            <Stack spacing={2}>
              {pageData!.tables.map((t) => (
                <TableWidget key={t.table_id} spec={t}
                  dataset={t.dataset ? pageData!.datasets[t.dataset] : undefined} />
              ))}
            </Stack>
          )}

          {!dataQ.isLoading && !pageData?.cards.length && !pageData?.charts.length && !pageData?.tables.length && (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body2">Sem widgets para esta página.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!exportToast}
        autoHideDuration={4000}
        onClose={() => setExportToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={exportToast?.startsWith("CSV") ? "success" : "info"}
          variant="filled"
          onClose={() => setExportToast(null)}
        >
          {exportToast}
        </Alert>
      </Snackbar>

      <Card sx={{
        background: "linear-gradient(135deg, rgba(252,171,16,0.08) 0%, rgba(64,120,153,0.05) 100%)",
        borderColor: "secondary.main", border: "1px solid",
      }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AutoAwesomeRoundedIcon sx={{ color: "secondary.dark" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "primary.dark" }}>Sophia sugere</Typography>
          </Stack>
          <Typography variant="body2">
            Use o painel Sophia à direita para <strong>trocar mês</strong>, <strong>navegar entre abas</strong>,
            <strong> aplicar filtros</strong> ou pedir explicações contextuais.
          </Typography>
        </CardContent>
      </Card>
    </>
  );
}
